import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/header";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { AnswerSplitDonut, RankedBarChart, SessionsTrendCard } from "@/components/analytics/charts";
import {
  Users,
  BookOpen,
  Gamepad2,
  ArrowLeft,
  LayoutDashboard,
  Trophy,
  TrendingUp,
  TrendingDown,
  Crown,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminPage,
});

function AdminPage() {
  const { isAdmin, user, loading } = useAuth();
  const [stats, setStats] = useState({ users: 0, quizzes: 0, sessions: 0 });
  const [recentQuizzes, setRecentQuizzes] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [sessionLogs, setSessionLogs] = useState([]);
  const [allParticipants, setAllParticipants] = useState([]);
  const [allResponses, setAllResponses] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const [u, q, s] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }),
        supabase.from("quizzes").select("id", { count: "exact", head: true }),
        supabase.from("sessions").select("id", { count: "exact", head: true }),
      ]);
      setStats({ users: u.count ?? 0, quizzes: q.count ?? 0, sessions: s.count ?? 0 });
      const { data: rq } = await supabase
        .from("quizzes")
        .select("*, profiles!quizzes_creator_id_fkey(name)")
        .order("created_at", { ascending: false })
        .limit(10);
      setRecentQuizzes(rq ?? []);
      const { data: ru } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      setRecentUsers(ru ?? []);
      const { data: logs } = await supabase
        .from("sessions")
        .select(
          "id, quiz_id, pin_code, status, created_at, ended_at, quizzes(title, profiles!quizzes_creator_id_fkey(name)), participants(id, nickname, score)",
        )
        .order("created_at", { ascending: false })
        .limit(50);
      setSessionLogs(logs ?? []);
      const [{ data: parts }, { data: resps }, { data: qs }] = await Promise.all([
        supabase.from("participants").select("id, session_id, nickname, score"),
        supabase.from("responses").select("question_id, is_correct"),
        supabase.from("questions").select("id, quiz_id, points"),
      ]);
      setAllParticipants(parts ?? []);
      setAllResponses(resps ?? []);
      setAllQuestions(qs ?? []);
    })();
  }, [isAdmin]);

  const quizAnalytics = useMemo(() => {
    const byQuiz = new Map();
    const quizMax = new Map();
    for (const q of allQuestions) {
      quizMax.set(q.quiz_id, (quizMax.get(q.quiz_id) ?? 0) + (q.points ?? 0));
    }
    const sessionQuiz = new Map();
    for (const s of sessionLogs) sessionQuiz.set(s.id, s.quiz_id);
    for (const s of sessionLogs) {
      const key = s.quiz_id;
      const existing = byQuiz.get(key) ?? {
        title: s.quizzes?.title ?? "—",
        plays: 0,
        scoreSum: 0,
        scoreCount: 0,
        correct: 0,
        total: 0,
        topWinner: null,
      };
      existing.plays += 1;
      const sessionParts = s.participants ?? [];
      for (const p of sessionParts) {
        const max = quizMax.get(key) ?? 0;
        if (max > 0) {
          existing.scoreSum += (p.score / max) * 100;
          existing.scoreCount += 1;
        }
        if (!existing.topWinner || p.score > existing.topWinner.score) {
          existing.topWinner = { name: p.nickname, score: p.score };
        }
      }
      byQuiz.set(key, existing);
    }
    const qToQuiz = new Map();
    for (const q of allQuestions) qToQuiz.set(q.id, q.quiz_id);
    for (const r of allResponses) {
      const qz = qToQuiz.get(r.question_id);
      if (!qz) continue;
      const e = byQuiz.get(qz);
      if (!e) continue;
      e.total += 1;
      if (r.is_correct) e.correct += 1;
    }
    return [...byQuiz.entries()].map(([id, v]) => ({
      id,
      title: v.title,
      plays: v.plays,
      avgPct: v.scoreCount > 0 ? Math.round(v.scoreSum / v.scoreCount) : 0,
      accuracy: v.total > 0 ? Math.round((v.correct / v.total) * 100) : 0,
      topWinner: v.topWinner,
    }));
  }, [sessionLogs, allQuestions, allResponses]);

  const bestQuizzes = [...quizAnalytics]
    .filter((q) => q.plays > 0)
    .sort((a, b) => b.avgPct - a.avgPct)
    .slice(0, 5);
  const hardestQuizzes = [...quizAnalytics]
    .filter((q) => q.plays > 0)
    .sort((a, b) => a.accuracy - b.accuracy)
    .slice(0, 5);
  const topPlayers = useMemo(() => {
    const map = new Map();
    for (const p of allParticipants) map.set(p.nickname, (map.get(p.nickname) ?? 0) + p.score);
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [allParticipants]);

  const answerSplit = useMemo(() => {
    let correct = 0;
    let incorrect = 0;
    for (const r of allResponses) {
      if (r.is_correct) correct += 1;
      else incorrect += 1;
    }
    return { correct, incorrect };
  }, [allResponses]);

  const mostPlayed = useMemo(
    () =>
      [...quizAnalytics]
        .filter((q) => q.plays > 0)
        .sort((a, b) => b.plays - a.plays)
        .slice(0, 6)
        .map((q) => ({ name: q.title, value: q.plays })),
    [quizAnalytics],
  );

  const topPlayerRows = useMemo(
    () => topPlayers.slice(0, 6).map(([name, score]) => ({ name, value: score })),
    [topPlayers],
  );

  if (loading)
    return (
      <div className="min-h-screen grid place-items-center">
        <p>Loading...</p>
      </div>
    );
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-bg">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <Card className="max-w-md mx-auto p-8">
            <h2 className="font-bold text-xl">Admins only</h2>
            <p className="text-sm text-muted mt-2">
              You don't have the admin role yet. Ask an existing admin to grant you the role, or run
              this in the Cloud SQL editor:
            </p>
            <pre className="mt-3 text-xs bg-surface-2 p-3 rounded text-left overflow-auto">{`INSERT INTO public.user_roles (user_id, role)\nVALUES ('${user?.id ?? "<your-user-id>"}', 'admin');`}</pre>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted">Admin</p>
            <h1 className="text-3xl font-bold tracking-tight">Platform overview</h1>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard">
                <ArrowLeft className="size-4" /> Back to dashboard
              </Link>
            </Button>
            <Button asChild size="sm" className="bg-gradient-primary">
              <Link to="/dashboard">
                <LayoutDashboard className="size-4" /> My quizzes
              </Link>
            </Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Users" value={stats.users} />
          <StatCard icon={BookOpen} label="Quizzes" value={stats.quizzes} />
          <StatCard icon={Gamepad2} label="Total sessions" value={stats.sessions} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <SessionsTrendCard sessions={sessionLogs} />
          <AnswerSplitDonut correct={answerSplit.correct} incorrect={answerSplit.incorrect} />
          <RankedBarChart
            title="Most played quizzes"
            caption="Sessions hosted per quiz"
            rows={mostPlayed}
            unit="plays"
          />
          <RankedBarChart
            title="Top players"
            caption="Cumulative points across all sessions"
            rows={topPlayerRows}
            unit="pts"
            color="var(--chart-2)"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h2 className="font-bold">Recent quizzes</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {recentQuizzes.map((q) => (
                <li key={q.id} className="flex justify-between gap-2 border-b pb-2">
                  <span className="min-w-0 truncate">{q.title}</span>
                  <span className="shrink-0 text-muted">{q.profiles?.name ?? "—"}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-5">
            <h2 className="font-bold">Recent users</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {recentUsers.map((u) => (
                <li key={u.id} className="flex justify-between gap-2 border-b pb-2">
                  <span className="min-w-0 truncate">{u.name}</span>
                  <span className="shrink-0 max-w-[55%] truncate text-muted">{u.email}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="p-5">
            <h2 className="font-bold flex items-center gap-2">
              <TrendingUp className="size-4 text-success" /> Quizzes players win most
            </h2>
            <p className="text-xs text-muted">Highest average score across all sessions</p>
            <ul className="mt-3 space-y-2 text-sm">
              {bestQuizzes.length === 0 && <li className="text-muted">No data yet.</li>}
              {bestQuizzes.map((q) => (
                <li key={q.id} className="flex justify-between gap-2 border-b pb-2">
                  <span className="min-w-0 line-clamp-1">{q.title}</span>
                  <span className="shrink-0 font-semibold text-success">{q.avgPct}/100</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-5">
            <h2 className="font-bold flex items-center gap-2">
              <TrendingDown className="size-4 text-destructive" /> Hardest quizzes
            </h2>
            <p className="text-xs text-muted">Lowest answer accuracy</p>
            <ul className="mt-3 space-y-2 text-sm">
              {hardestQuizzes.length === 0 && <li className="text-muted">No data yet.</li>}
              {hardestQuizzes.map((q) => (
                <li key={q.id} className="flex justify-between gap-2 border-b pb-2">
                  <span className="min-w-0 line-clamp-1">{q.title}</span>
                  <span className="shrink-0 font-semibold text-destructive">
                    {q.accuracy}% correct
                  </span>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-5">
            <h2 className="font-bold flex items-center gap-2">
              <Crown className="size-4 text-warning" /> Top players (all-time)
            </h2>
            <p className="text-xs text-muted">Cumulative points across all sessions</p>
            <ul className="mt-3 space-y-2 text-sm">
              {topPlayers.length === 0 && <li className="text-muted">No data yet.</li>}
              {topPlayers.map(([name, score], i) => (
                <li key={name} className="flex justify-between gap-2 border-b pb-2">
                  <span className="min-w-0 truncate">
                    <span className="font-bold mr-2">#{i + 1}</span>
                    {name}
                  </span>
                  <span className="shrink-0 font-semibold">{score} pts</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <Card className="p-5">
          <h2 className="font-bold flex items-center gap-2">
            <Trophy className="size-4 text-warning" /> Game history (all sessions)
          </h2>
          <p className="text-xs text-muted">Latest 50 hosted sessions across the platform</p>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted">
                <tr>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Quiz</th>
                  <th className="py-2 pr-3">Host</th>
                  <th className="py-2 pr-3">Winner</th>
                  <th className="py-2 pr-3">PIN</th>
                  <th className="py-2 pr-3">Players</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Ended</th>
                </tr>
              </thead>
              <tbody>
                {sessionLogs.map((s) => {
                  const parts = s.participants ?? [];
                  const winner = parts.length
                    ? [...parts].sort((a, b) => b.score - a.score)[0]
                    : null;
                  return (
                    <tr key={s.id} className="border-t">
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3">
                        <Link
                          to="/results/$sessionId"
                          params={{ sessionId: s.id }}
                          className="hover:underline"
                        >
                          {s.quizzes?.title ?? "—"}
                        </Link>
                      </td>
                      <td className="py-2 pr-3 text-muted">{s.quizzes?.profiles?.name ?? "—"}</td>
                      <td className="py-2 pr-3">
                        {winner ? (
                          <span className="flex items-center gap-1">
                            <Crown className="size-3 text-warning" />
                            {winner.nickname} ({winner.score})
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono">{s.pin_code}</td>
                      <td className="py-2 pr-3">{parts.length}</td>
                      <td className="py-2 pr-3">
                        <Badge variant={s.status === "ended" ? "secondary" : "default"}>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-muted whitespace-nowrap">
                        {s.ended_at ? new Date(s.ended_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  );
                })}
                {sessionLogs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-muted">
                      No sessions yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-5 bg-gradient-card border-2">
      <Icon className="size-5 text-accent" />
      <p className="text-xs text-muted mt-2">{label}</p>
      <p className="text-3xl font-bold font-display">{value}</p>
    </Card>
  );
}
